import React, { useEffect, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import toast from "react-hot-toast";
import { Contact, LayoutPanelTop } from "lucide-react";
import { useRole } from "../MangeContextProvide";
import { Flag } from "./type";
import FlagFormDrawer from "./FlagFormDrawer";
import "./ManageFlags.css";
import {
  fetchFlags,
  createFlag,
  updateFlag,
  fetchAttachedFlagCounts,
  setFlagStatus,
} from "./manageFlags.service";
import SearchInput from "../../common/SearchInput";

function ManageFlags() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedFlag, setEditedFlag] = useState<Flag | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [communicationFlagCount, setCommunicationFlagCount] = useState(0);
  const [oceanCommunicationFlagCount, OceanSetCommunicationFlagCount] = useState(0);
  const [contactFlagCount, setContactFlagCount] = useState(0);
  const [deactivateId, setDeactivateId] = useState<number | null>(null);

  // Drawer visibility (replaces the old inline-row edit mode)
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const { globleCustomerId } = useRole() as any;

  const isFirstRender = useRef(true);
  const latestSearchRef = useRef(0);

  // ── Open drawer for edit ──
  const handleEdit = (flag: Flag) => {
    setEditingId(flag.id);
    setEditedFlag({
      ...flag,
      added_date: flag.added_date,
    });
    setIsAddingNew(false);
    setShowValidation(false);
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      setShowValidation(true);
      if (!editedFlag) return;
      if (!editedFlag?.name?.trim()) {
        return;
      }

      if (isAddingNew) {
        const payload = {
          name: editedFlag.name,
          category: editedFlag.category,
          description: editedFlag.description,
          hex_color_code: editedFlag.hex_color_code,
          is_active: editedFlag.status === "Active",
        };

        const { data } = { data: await createFlag(payload) };

        getFlags(searchTerm);
        setShowValidation(false);
        toast.success(data.message || "Flag updated successfully");
      } else {
        const payload = {
          id: editedFlag.id,
          name: editedFlag.name,
          category: editedFlag.category,
          description: editedFlag.description,
          hex_color_code: editedFlag.hex_color_code,
          is_active: editedFlag.status === "Active",
        };

        const { data } = { data: await updateFlag(payload) };
        getFlags(searchTerm);
        toast.success(data.message || "Flag updated successfully");
      }

      setEditedFlag(null);
      setIsAddingNew(false);
      setEditingId(null);
      setDrawerOpen(false);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }
    }
  };

  // ── Close drawer / cancel ──
  const handleCancel = () => {
    setShowValidation(false);
    setIsAddingNew(false);
    setOpenConfirm(false);
    setEditingId(null);
    setEditedFlag(null);
    setDrawerOpen(false);
  };

  const handleAddFlag = () => {
    // Prevent adding while already adding or editing
    if (isAddingNew || editingId !== null) {
      return;
    }

    const newFlag: Flag = {
      id: Date.now(),
      sNo: flags.length + 1,
      name: "",
      hex_color_code: "#000000",
      description: "",
      status: "Active",
      customer: globleCustomerId,
      category: "Branch",
    };

    // Drawer flow: no longer inject a blank row into the table.
    setEditedFlag(newFlag);
    setEditingId(newFlag.id);
    setIsAddingNew(true);
    setShowValidation(false);
    setDrawerOpen(true);
  };

  async function getFlags(search = "") {
    const requestId = ++latestSearchRef.current;
    setLoading(true);
    try {
      const data = await fetchFlags(search);

      // Ignore stale responses that resolved after a newer request fired.
      if (requestId !== latestSearchRef.current) return;

      setFlags(data.data);
    } catch (error) {
      if (requestId !== latestSearchRef.current) return;
      setFlags([]);
    } finally {
      if (requestId === latestSearchRef.current) {
        setLoading(false);
      }
    }
  }

  async function getCountofAttachedFlags(id: any) {
    try {
      const data = await fetchAttachedFlagCounts(id);
      setCommunicationFlagCount(data?.data?.communicationFlags || 0);
      setContactFlagCount(data?.data?.contactsFlag || 0);
      OceanSetCommunicationFlagCount(data?.data?.oceanCommunicationFlags || 0);
    } catch (error) { }
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      getFlags("");
      return;
    }

    const handler = setTimeout(() => {
      setPage(0);
      getFlags(searchTerm);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleInputChange = (field: keyof Flag, value: string) => {
    if (field === "name") {
      value = value.replace(/[^a-zA-Z0-9 _ -]/g, "");
    }

    if (editedFlag) {
      setEditedFlag({
        ...editedFlag,
        [field]: value,
      });
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isAddingNew) {
      const nextStatus = isChecked ? "Active" : "Deactivated";
      if (editedFlag) {
        setEditedFlag({ ...editedFlag, status: nextStatus });
      }
      setFlags((prev) =>
        prev.map((f) => (f.id === editingId ? { ...f, status: nextStatus } : f))
      );
      return;
    }

    if (!isChecked) {
      if (editingId != null) {
        setDeactivateId(editingId);
        getCountofAttachedFlags(editingId);
      }
      setOpenConfirm(true);
    } else {
      if (editedFlag) {
        setEditedFlag({ ...editedFlag, status: "Active" });
      }
      setFlags((prev) =>
        prev.map((f) => (f.id === editingId ? { ...f, status: "Active" } : f))
      );
    }
  };

  const updateFlagStatusApi = async (id: number, status: boolean) => {
    try {
      const response = await setFlagStatus(id, status);
      toast.success(status ? "Activated" : "Deactivated");
      return response;
    } catch (error: any) {
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }
      throw error;
    }
  };

  const handleStatusChangess = async (
    event: React.ChangeEvent<HTMLInputElement>,
    flag: any
  ) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      setDeactivateId(flag.id);
      getCountofAttachedFlags(flag.id);
      setOpenConfirm(true);
      return;
    }

    try {
      await updateFlagStatusApi(flag.id, true);
      setFlags((prev) =>
        prev.map((f) => (f.id === flag.id ? { ...f, status: "Active" } : f))
      );
    } catch (error) { }
  };

  const confirmDeactivate = async () => {
    if (!deactivateId) return;

    try {
      await updateFlagStatusApi(deactivateId, false);

      setFlags((prev) =>
        prev.map((f) =>
          f.id === deactivateId ? { ...f, status: "Deactivated" } : f
        )
      );

      setEditedFlag((prev) =>
        prev && prev.id === deactivateId ? { ...prev, status: "Deactivated" } : prev
      );
    } catch (error) {
    } finally {
      setOpenConfirm(false);
      setDeactivateId(null);
    }
  };

  const cancelDeactivate = () => {
    setOpenConfirm(false);
    setDeactivateId(null);
    setCommunicationFlagCount(0);
    OceanSetCommunicationFlagCount(0);
    setContactFlagCount(0);
  };

  // Status filter applied client-side (search is backend-driven).
  const filteredFlags = flags.filter((flag) => {
    const matchesStatus =
      statusFilter === "All" ? true : flag.status === statusFilter;
    return matchesStatus;
  });

  const paginatedFlags = filteredFlags.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const pageCount = Math.max(1, Math.ceil(filteredFlags.length / rowsPerPage));

  return (
    <>

      <div className="ctpl-card">
        {/* Toolbar */}
        <div className="ctpl-toolbar">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search flags…"
          />

          <div className="ctpl-filter-chips">
            {["All", "Active", "Deactivated"].map((s) => (
              <button
                key={s}
                className={`ctpl-filter-chip${statusFilter === s ? " active" : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="ctpl-spacer" />

          <button
            className="ctpl-btn ctpl-btn-primary"
            onClick={handleAddFlag}
            disabled={isAddingNew || editingId !== null}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Flag
          </button>
        </div>

        {/* Table */}
        <div className="ctpl-table-scroll">
          <table className="ctpl-table">
            <thead>
              <tr>
                <th style={{ width: "60px" }}>S.No</th>
                <th>Flag Name</th>
                <th>Category</th>
                <th>Hex Color</th>
                <th>Description</th>
                <th>Added on</th>
                <th>Status</th>
                <th style={{ textAlign: "right", paddingRight: 22 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && flags.length === 0 ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={8}>
                    Loading flags…
                  </td>
                </tr>
              ) : filteredFlags.length === 0 ? (
                <tr>
                  <td className="ctpl-empty-cell" colSpan={8}>
                    No flags found
                  </td>
                </tr>
              ) : (
                paginatedFlags.map((flag, index) => {
                  const isOwner = globleCustomerId && globleCustomerId === flag.customer;
                  return (
                    <tr key={flag.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="ctpl-tmpl-name">{flag.name}</div>
                      </td>
                      <td>{flag.category}</td>
                      <td>
                        <div className="mf-color-cell">
                          <span
                            className="mf-color-swatch"
                            style={{ backgroundColor: flag.hex_color_code || "transparent" }}
                          />
                          <span className="mf-color-hex">{flag.hex_color_code}</span>
                        </div>
                      </td>
                      <td style={{ maxWidth: 260 }}>{flag.description}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{flag.added_date}</td>
                      <td>
                        <Switch
                          className="thinSwitch"
                          checked={flag.status === "Active"}
                          onChange={(e) => handleStatusChangess(e, flag)}
                          color="primary"
                        />
                      </td>
                      <td>
                        <div className="ctpl-row-actions">
                          {isOwner && (
                            <button
                              className="ctpl-action-circle edit"
                              title={flag.status === "Deactivated" ? "Activate to edit" : "Edit"}
                              disabled={flag.status === "Deactivated"}
                              onClick={() => handleEdit(flag)}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        <div className="ctpl-table-footer">
          <span>
            {filteredFlags.length} flag{filteredFlags.length === 1 ? "" : "s"}
          </span>
          <div className="ctpl-pagination">
            <button
              className="ctpl-page-btn"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ‹
            </button>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <button
              className="ctpl-page-btn"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Add / Edit drawer */}
      <FlagFormDrawer
        open={drawerOpen}
        isAddingNew={isAddingNew}
        editedFlag={editedFlag}
        showValidation={showValidation}
        onFieldChange={handleInputChange}
        onStatusChange={handleStatusChange}
        onClose={handleCancel}
        onSave={handleSave}
      />

      {/* Deactivation confirm */}
      <div
        className={`mf-confirm-overlay${openConfirm ? " open" : ""}`}
        onClick={cancelDeactivate}
      />
      <div className={`mf-confirm${openConfirm ? " open" : ""}`} role="dialog" aria-modal="true">
        <div className="mf-confirm-icon">
          <LayoutPanelTop size={24} />
        </div>
        <h2 className="mf-confirm-title">Confirm Deactivation</h2>
        <p className="mf-confirm-text">Are you sure you want to deactivate this flag?</p>

        {(communicationFlagCount !== 0 ||
          oceanCommunicationFlagCount !== 0 ||
          contactFlagCount !== 0) && (
            <div className="mf-confirm-counts">
              {communicationFlagCount !== 0 && (
                <div className="mf-count-row">
                  <LayoutPanelTop size={18} />
                  <span>
                    Communication Templates attached: <strong>{communicationFlagCount}</strong>
                  </span>
                </div>
              )}
              {oceanCommunicationFlagCount !== 0 && (
                <div className="mf-count-row">
                  <LayoutPanelTop size={18} />
                  <span>
                    Ocean Communication Templates attached:{" "}
                    <strong>{oceanCommunicationFlagCount}</strong>
                  </span>
                </div>
              )}
              {contactFlagCount !== 0 && (
                <div className="mf-count-row">
                  <Contact size={18} />
                  <span>
                    Contacts attached: <strong>{contactFlagCount}</strong>
                  </span>
                </div>
              )}
            </div>
          )}

        <div className="mf-confirm-actions">
          <button className="ctpl-btn ctpl-btn-ghost" onClick={cancelDeactivate}>
            No
          </button>
          <button className="ctpl-btn mf-btn-danger" onClick={confirmDeactivate}>
            Yes, Deactivate
          </button>
        </div>
      </div>
    </>
  );
}

export default ManageFlags;